import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, User, Mail, Check, AlertTriangle, Trash2, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "#4FA3D9";

export default function AccountSettings() {
  const navigate = useNavigate();
  const { account, loading, updateName, requestDeletionCode, deleteAccount } = useAuth();

  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState("");

  // Delete flow: step "confirm" (type DELETE) -> step "verify" (email code).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [step, setStep] = useState("confirm");
  const [confirmText, setConfirmText] = useState("");
  const [code, setCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    // While deleting, auth becomes null intentionally — don't bounce to /signin;
    // confirmDelete redirects to the public homepage itself.
    if (!loading && !account && !deleting) navigate("/signin", { replace: true });
  }, [loading, account, deleting, navigate]);

  useEffect(() => {
    if (account) setName(account.name || "");
  }, [account]);

  if (loading || !account) {
    return (
      <div className="min-h-[100dvh] w-full bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center py-40 text-white/60" data-testid="account-settings-loading">
          <Loader2 size={20} className="animate-spin" style={{ color: ACCENT }} />
        </div>
      </div>
    );
  }

  const trimmed = name.trim();
  const nameDirty = trimmed !== (account.name || "").trim();
  const nameValid = trimmed.length > 0;

  const saveName = async () => {
    setNameError("");
    if (!nameValid) { setNameError("Please enter your name."); return; }
    setSavingName(true);
    try {
      await updateName(trimmed);
      setName(trimmed);
      toast.success("Name updated");
    } catch (e) {
      setNameError(e.message || "Could not save name");
    }
    setSavingName(false);
  };

  const openModal = () => {
    setStep("confirm"); setConfirmText(""); setCode(""); setDeleteError(""); setConfirmOpen(true);
  };
  const closeModal = () => { if (!sendingCode && !deleting) setConfirmOpen(false); };

  const sendCode = async () => {
    if (confirmText !== "DELETE") return;
    setSendingCode(true); setDeleteError("");
    try {
      await requestDeletionCode();
      setStep("verify");
      toast.success(`Verification code sent to ${account.email}`);
    } catch (e) {
      setDeleteError(e.message || "Could not send verification code");
    }
    setSendingCode(false);
  };

  const resendCode = async () => {
    setSendingCode(true); setDeleteError("");
    try {
      await requestDeletionCode();
      setCode("");
      toast.success("A new code has been sent");
    } catch (e) {
      setDeleteError(e.message || "Could not resend code");
    }
    setSendingCode(false);
  };

  const codeValid = /^\d{6}$/.test(code);

  const confirmDelete = async () => {
    if (!codeValid) return;
    setDeleting(true); setDeleteError("");
    try {
      await deleteAccount(code);
      toast.success("Your account has been deleted");
      navigate("/", { replace: true });
    } catch (e) {
      setDeleting(false);
      setDeleteError(e.message || "Account deletion could not be completed");
    }
  };

  return (
    <div className="relative min-h-[100dvh] w-full bg-[#0a0a0a]">
      <Navbar />
      <div className="relative z-10 mx-auto w-full max-w-2xl px-6 py-16 sm:py-20" data-testid="account-settings-page">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/50">Account</span>
          <h1 className="mb-10 mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Account settings</h1>

          {/* NAME — editable */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-5" data-testid="account-name-row">
            <div className="mb-2.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/40">
              <User size={14} /> Name
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                type="text"
                value={name}
                maxLength={80}
                onChange={(e) => { setName(e.target.value); if (nameError) setNameError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter" && nameDirty && nameValid && !savingName) saveName(); }}
                data-testid="account-name-input"
                placeholder="Your name"
                className="w-full flex-1 rounded-xl border border-white/12 bg-black/30 px-4 py-3 text-base text-white outline-none transition-colors placeholder:text-white/30 focus:border-[#4FA3D9]/60"
              />
              <button
                onClick={saveName}
                disabled={!nameDirty || !nameValid || savingName}
                data-testid="account-name-save-btn"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                style={{ background: ACCENT }}
              >
                {savingName ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save
              </button>
            </div>
            {nameError && <p className="mt-2 text-sm text-red-400" data-testid="account-name-error">{nameError}</p>}
          </div>

          {/* EMAIL — read-only */}
          <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-5" data-testid="account-email-row">
            <div className="mb-2.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/40">
              <Mail size={14} /> Email
            </div>
            <p className="text-base text-white/90" data-testid="account-email-value">{account.email || "—"}</p>
            <p className="mt-1.5 text-sm text-white/40" data-testid="account-email-helper">Your verified sign-in email.</p>
          </div>

          {/* DELETE ACCOUNT — danger zone */}
          <div className="mt-14 rounded-2xl border border-red-500/20 bg-red-500/[0.04] px-5 py-5" data-testid="account-danger-zone">
            <div className="mb-1.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-red-400/80">
              <AlertTriangle size={14} /> Delete account
            </div>
            <p className="text-sm leading-relaxed text-white/55">
              Permanently delete your Adaptience account. This removes your account and signs you out everywhere.
              Your saved Practice History will no longer be accessible. This cannot be undone.
            </p>
            <button
              onClick={openModal}
              data-testid="account-delete-open-btn"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-red-500/40 px-6 py-3 text-sm font-medium text-red-300 transition-colors hover:bg-red-500/10"
            >
              <Trash2 size={16} /> Delete account
            </button>
          </div>
        </motion.div>
      </div>

      {/* Confirmation modal — step 1: type DELETE, step 2: email verification code */}
      <AnimatePresence>
        {confirmOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="account-delete-modal"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeModal} />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 26 }}
              className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#111213] p-7 shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
            >
              <button
                onClick={closeModal}
                data-testid="account-delete-close-btn"
                className="absolute right-5 top-5 text-white/40 transition-colors hover:text-white/80"
                aria-label="Close"
              >
                <X size={18} />
              </button>

              {step === "confirm" ? (
                <>
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-500/15">
                    <AlertTriangle size={20} className="text-red-400" />
                  </div>
                  <h2 className="mt-4 font-display text-xl font-semibold text-white">Permanently delete your account?</h2>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/55">
                    This is permanent and cannot be undone. You will lose access to your saved
                    Adaptience Practice History. To continue, type DELETE — we'll then email a
                    verification code to <span className="text-white/80">{account.email}</span>.
                  </p>
                  <label htmlFor="delete-confirm-input" className="mt-5 block text-sm text-white/70">
                    Type <span className="font-semibold text-white">DELETE</span> to continue
                  </label>
                  <input
                    id="delete-confirm-input"
                    type="text"
                    value={confirmText}
                    autoComplete="off"
                    autoFocus
                    onChange={(e) => { setConfirmText(e.target.value); if (deleteError) setDeleteError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter" && confirmText === "DELETE" && !sendingCode) sendCode(); }}
                    data-testid="account-delete-confirm-input"
                    className="mt-2 w-full rounded-xl border border-white/12 bg-black/30 px-4 py-3 text-base text-white outline-none transition-colors placeholder:text-white/25 focus:border-red-500/60"
                    placeholder="DELETE"
                  />
                  {deleteError && <p className="mt-2 text-sm text-red-400" data-testid="account-delete-error">{deleteError}</p>}
                  <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                      onClick={closeModal}
                      disabled={sendingCode}
                      data-testid="account-delete-cancel-btn"
                      className="rounded-full px-5 py-2.5 text-sm font-medium text-white/70 transition-colors hover:text-white disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendCode}
                      disabled={confirmText !== "DELETE" || sendingCode}
                      data-testid="account-delete-send-code-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition-opacity hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {sendingCode ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                      {sendingCode ? "Sending…" : "Send verification code"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-500/15">
                    <ShieldCheck size={20} className="text-red-400" />
                  </div>
                  <h2 className="mt-4 font-display text-xl font-semibold text-white">Enter verification code</h2>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/55">
                    We sent a 6-digit code to <span className="text-white/80">{account.email}</span>.
                    Enter it below to permanently delete your account. The code expires in 10 minutes.
                  </p>
                  <label htmlFor="delete-code-input" className="mt-5 block text-sm text-white/70">Verification code</label>
                  <input
                    id="delete-code-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    autoFocus
                    onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); if (deleteError) setDeleteError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter" && codeValid && !deleting) confirmDelete(); }}
                    data-testid="account-delete-code-input"
                    className="mt-2 w-full rounded-xl border border-white/12 bg-black/30 px-4 py-3 text-center text-lg tracking-[0.5em] text-white outline-none transition-colors placeholder:tracking-normal placeholder:text-white/25 focus:border-red-500/60"
                    placeholder="000000"
                  />
                  {deleteError && <p className="mt-2 text-sm text-red-400" data-testid="account-delete-error">{deleteError}</p>}
                  <button
                    onClick={resendCode}
                    disabled={sendingCode || deleting}
                    data-testid="account-delete-resend-btn"
                    className="mt-3 text-sm text-white/50 underline-offset-2 transition-colors hover:text-white/80 disabled:opacity-50"
                  >
                    {sendingCode ? "Sending…" : "Resend code"}
                  </button>
                  <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                      onClick={closeModal}
                      disabled={deleting}
                      data-testid="account-delete-cancel-btn"
                      className="rounded-full px-5 py-2.5 text-sm font-medium text-white/70 transition-colors hover:text-white disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDelete}
                      disabled={!codeValid || deleting}
                      data-testid="account-delete-confirm-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition-opacity hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                      {deleting ? "Deleting…" : "Delete permanently"}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
